import { createReducer } from '../reducerUtil'
import {
  SET_INPUT_SELECT_MODE,
  SET_INPUTS_SELECTED,
  SET_INPUT_SEARCH,
  SET_INPUT_SEARCH_MODE,
  START_HIGHLIGHT,
  UPDATE_HIGHLIGHT_END,
  CLEAR_HIGHLIGHT,
  HIGHLIGHT_ALL,
  SET_INPUT_MANUAL_SORT_MODE,
  SET_HIGHLIGHT_MODE,
  SET_IMAGE_QUALITY,
  SET_LAST_FOCUSED_INPUT,
} from './constants'
import { isMobile } from 'react-device-detect'

const initialState = {
  inputSelectMode: false,
  inputsSelected: [],
  inputSearchMode: false,
  inputSearch: '',
  inputManualSortMode: false, // for manual sort input
  inputVisibleColumns: [],
  inputHighlights: [], // e.g. [{ start: { rowId: X, colKey: X }, end: { rowId: X, colKey: X } }],
  highlightMode: 'normal', // OR delete (double click delete), to set the highlight color
  imageQuality: 200, // 200 (200px) or 720 (720p) for upload image size option
  lastFocusedInput: null, // focus back when duplicate or delete input/ detect cmd select all last click at which
}

export const setInputSelectMode = (state, payload) => {
  return {
    ...state,
    inputSelectMode: payload.status,
  }
}

export const setInputsSelected = (state, payload) => {
  return {
    ...state,
    inputsSelected: payload.inputsSelected,
  }
}

export const setInputSearchMode = (state, payload) => ({
  ...state,
  inputSearchMode: payload.status,
  inputSearch: payload.status ? state.inputSearch : '',
})

export const setInputSearch = (state, payload) => {
  return {
    ...state,
    inputSearch: payload.inputSearch,
  }
}

export const startHighlight = (state, payload) => {
  const newRange = {
    start: { rowId: payload.rowId, colKey: payload.colKey },
    end: payload.isCmdPressed
      ? { rowId: payload.rowId, colKey: payload.colKey }
      : null,
  }

  let highlights = [...state.inputHighlights]
  // If CMD is pressed, we might need to complete the previous highlight
  if (payload.isCmdPressed) {
    const lastIndex = highlights.length - 1
    if (lastIndex >= 0 && highlights[lastIndex].end === null) {
      highlights[lastIndex] = {
        ...highlights[lastIndex],
        end: highlights[lastIndex].start,
      }
    }

    const rowIds = getHighlightedRowIds(state.inputHighlights)
    if (rowIds.includes(payload.rowId)) {
      // If the clicked row is already highlighted, don't add a new highlight
      return {
        ...state,
        highlightMode: 'normal',
      }
    }
  }

  return {
    ...state,
    inputVisibleColumns: payload.inputVisibleColumns,
    inputHighlights: payload.isCmdPressed
      ? [...highlights, newRange] // add
      : [newRange], // replace
    highlightMode: 'normal',
  }
}

// Check which rows are currently highlighted
const getHighlightedRowIds = (inputHighlights, excludeIndex) => {
  const rowSet = new Set()

  inputHighlights.forEach((item, idx) => {
    if (idx === excludeIndex) return
    const start = item.start.rowId
    const end = item.end ? item.end.rowId : start

    const min = Math.min(start, end)
    const max = Math.max(start, end)

    for (let i = min; i <= max; i++) {
      rowSet.add(i)
    }
  })

  return [...rowSet].sort((a, b) => a - b)
}

const getFirstFreeRange = (min, max, usedRows) => {
  const usedSet = new Set(usedRows)

  const originalMin = min
  const originalMax = max

  if (min > max) [min, max] = [max, min]

  let start = min

  while (usedSet.has(start) && start <= max) start++

  if (start > max) return null

  let end = start

  while (end + 1 <= max && !usedSet.has(end + 1)) end++

  if (originalMin > originalMax) {
    return { start: end, end: start }
  }

  return { start, end }
}

export const updateHighlightEnd = (state, payload) => {
  const index = state.inputHighlights.length - 1
  if (index < 0) return state

  const range = state.inputHighlights[index]
  if (!range?.start) return state

  let nextHighlights = [...state.inputHighlights]
  if (!isMobile && JSON.stringify(range.start) === JSON.stringify(payload)) {
    // for desktop when click any input of the highlighted, it will clear the highlight
    nextHighlights[index] = {
      ...range,
      end: null,
    }

    return {
      ...state,
      inputHighlights: nextHighlights,
      highlightMode: 'normal',
      inputVisibleColumns: [],
    }
  } else {
    if (payload.isCmdPressed) {
      // Adjust highlight range to avoid overlapping existing highlights
      const rowIds = getHighlightedRowIds(state.inputHighlights, index)
      const { start, end } = getFirstFreeRange(
        nextHighlights[index].start.rowId,
        payload.rowId,
        rowIds,
      )

      nextHighlights[index] = {
        start: {
          ...nextHighlights[index].start,
          rowId: start,
        },
        end: {
          rowId: end,
          colKey: payload.colKey,
        },
      }
    } else {
      nextHighlights[index] = {
        ...range,
        end: { rowId: payload.rowId, colKey: payload.colKey },
      }
    }

    return {
      ...state,
      inputHighlights: nextHighlights,
      highlightMode: 'normal',
    }
  }
}

export const clearHighlight = state => {
  return {
    ...state,
    inputHighlights: [],
    highlightMode: 'normal',
    inputVisibleColumns: [],
    lastFocusedInput: null,
  }
}

export const setInputManualSortMode = (state, payload) => ({
  ...state,
  inputManualSortMode: payload.status,
})

export const highlightAll = (state, payload) => {
  return {
    ...state,
    inputHighlights: [
      {
        start: { rowId: 0, colKey: payload.inputVisibleColumns[0] },
        end: {
          rowId: payload.size - 1,
          colKey:
            payload.inputVisibleColumns[payload.inputVisibleColumns.length - 1],
        },
        ...(state.lastFocusedInput !== null && {
          endIndex: state.lastFocusedInput,
        }),
      },
    ],
    highlightMode: 'normal',
  }
}

export const setHighlightMode = (state, payload) => ({
  ...state,
  highlightMode: payload.mode,
})

export const setImageQuality = (state, payload) => {
  return {
    ...state,
    imageQuality: payload.imageQuality,
  }
}

export const setLastFocusedInput = (state, payload) => {
  return {
    ...state,
    lastFocusedInput: payload.lastFocusedInput,
  }
}

export default createReducer(initialState, {
  [SET_INPUT_SELECT_MODE]: setInputSelectMode,
  [SET_INPUTS_SELECTED]: setInputsSelected,
  [SET_INPUT_SEARCH_MODE]: setInputSearchMode,
  [SET_INPUT_SEARCH]: setInputSearch,
  [START_HIGHLIGHT]: startHighlight,
  [UPDATE_HIGHLIGHT_END]: updateHighlightEnd,
  [CLEAR_HIGHLIGHT]: clearHighlight,
  [HIGHLIGHT_ALL]: highlightAll,
  [SET_INPUT_MANUAL_SORT_MODE]: setInputManualSortMode,
  [SET_HIGHLIGHT_MODE]: setHighlightMode,
  [SET_IMAGE_QUALITY]: setImageQuality,
  [SET_LAST_FOCUSED_INPUT]: setLastFocusedInput,
})
